import { MemberCountry } from '@data/utils/types';
import { productStore } from './productStore';
// Not the '@data' barrel: it drags a cycle in, and the enum then reads as undefined under test.

import { inVersion } from '../productMock';
import { SENIORITY_LEVELS, type Seniority } from '../../members/memberMock';

/** Keyed by the chapter's own key, so renaming its label leaves the prices alone. */
export type CostMatrix = Record<string, Partial<Record<Seniority, number>>>;

/** The API's own country values, not a second spelling; Belgium is what an unset country prices as. */
export const COST_COUNTRIES = [MemberCountry.belgium, MemberCountry.ukraine] as const;
export type CostCountry = MemberCountry;
export const DEFAULT_COST_COUNTRY: CostCountry = MemberCountry.belgium;

export const costMatrixStorageKey = (
  productId: string,
  country: CostCountry = DEFAULT_COST_COUNTRY,
  version = '',
): string => inVersion(`productCostMatrix:${productId}:${country}`, version);

// Read as Belgium's until Belgium's own key is written, so a product priced before this stays priced.
const legacyStorageKey = (productId: string): string => `productCostMatrix:${productId}`;

// A value that does not parse reads as empty.
const readRaw = (productId: string, country: CostCountry, version: string): unknown => {
  try {
    // Newest key first, then the ones a product may still be priced on.
    const raw =
      productStore.getItem(costMatrixStorageKey(productId, country, version)) ??
      productStore.getItem(costMatrixStorageKey(productId, country)) ??
      (country === DEFAULT_COST_COUNTRY ? productStore.getItem(legacyStorageKey(productId)) : null);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** EUR/h to the cent; a comma reads as a decimal point, since that is what these fields are typed with. */
export const asAmount = (value: unknown): number | null => {
  const amount = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100) / 100;
};

export const readCostMatrix = (
  productId: string,
  country: CostCountry = DEFAULT_COST_COUNTRY,
  version = '',
): CostMatrix => {
  const raw = readRaw(productId, country, version);
  if (!raw || typeof raw !== 'object') return {};
  const matrix: CostMatrix = {};
  Object.entries(raw as Record<string, unknown>).forEach(([chapter, levels]) => {
    if (!levels || typeof levels !== 'object') return;
    SENIORITY_LEVELS.forEach(level => {
      const amount = asAmount((levels as Record<string, unknown>)[level]);
      if (amount === null) return;
      matrix[chapter] = { ...matrix[chapter], [level]: amount };
    });
  });
  return matrix;
};

export const writeCostMatrix = (
  productId: string,
  matrix: CostMatrix,
  country: CostCountry = DEFAULT_COST_COUNTRY,
  version = '',
): void => {
  productStore.setItem(costMatrixStorageKey(productId, country, version), JSON.stringify(matrix));
  // Every version with no prices of its own still reads the legacy key, so it only goes with that write.
  if (country === DEFAULT_COST_COUNTRY && !version) productStore.removeItem(legacyStorageKey(productId));
};

/** A rate as money: whole euros stay whole, cents are always both digits, so a column reads as prices. */
export const formatRate = (amount: number): string => (Number.isInteger(amount) ? String(amount) : amount.toFixed(2));

export const costMatrixRate = (matrix: CostMatrix, chapter: string, level: string): number | null =>
  matrix[chapter]?.[level as Seniority] ?? null;

/** The grid as typed: every cell a string, so a half-typed amount survives a re-render. */
export type CostMatrixDraft = Record<string, Partial<Record<Seniority, string>>>;

export const draftFromMatrix = (matrix: CostMatrix): CostMatrixDraft => {
  const draft: CostMatrixDraft = {};
  Object.entries(matrix).forEach(([chapter, levels]) => {
    Object.entries(levels).forEach(([level, amount]) => {
      if (amount === undefined) return;
      draft[chapter] = { ...draft[chapter], [level as Seniority]: String(amount) };
    });
  });
  return draft;
};

/** A blank cell is "not priced" rather than zero, so clearing one removes it. */
export const matrixFromDraft = (draft: CostMatrixDraft): CostMatrix => {
  const matrix: CostMatrix = {};
  Object.entries(draft).forEach(([chapter, levels]) => {
    SENIORITY_LEVELS.forEach(level => {
      const typed = levels?.[level];
      if (typed === undefined || typed.trim() === '') return;
      const amount = asAmount(typed);
      if (amount === null) return;
      matrix[chapter] = { ...matrix[chapter], [level]: amount };
    });
  });
  return matrix;
};

/** Which cells hold something that is not a price, so Save can be held and they can be marked. */
export const invalidCells = (draft: CostMatrixDraft): string[] =>
  Object.entries(draft).flatMap(([chapter, levels]) =>
    SENIORITY_LEVELS.filter(level => {
      const typed = levels?.[level];
      return typed !== undefined && typed.trim() !== '' && asAmount(typed) === null;
    }).map(level => `${chapter}:${level}`),
  );
